import { API_ERROR_CODES, ApiCallError, type ProjectVersionResponse } from "@/api";

/**
 * The version a dataspace runs, as a choice: what the picker offers, and what it starts on.
 *
 * The server runs only a version whose build is on it (`version_not_built` for any other), so the
 * picker offers what `GET /projects/{id}/versions` says can run — `wip` when it is built, then
 * every release with a build, newest first. `wip` is offered even unbuilt: a new project's
 * dataspace is made before its first build, which the next edit starts, and the server accepts it.
 */

export const WIP = "wip";

export interface VersionOption {
	value: string;
	/** A release's version and title, or `wip`; the caller words `wip`. */
	version: string;
	title: string | null;
	kind: "wip" | "release" | "current";
}

/**
 * What the picker offers: `wip`, the runnable releases newest first, and — when the dataspace runs
 * something that is neither any more (its build was removed) — that too, so the picker never
 * shows a value it does not list.
 */
export function versionOptions(
	versions: ProjectVersionResponse[],
	current?: string | null,
): VersionOption[] {
	const options: VersionOption[] = [{ value: WIP, version: WIP, title: null, kind: "wip" }];
	for (const v of versions) {
		if (v.version === WIP) continue;
		options.push({ value: v.version, version: v.version, title: v.title, kind: "release" });
	}
	if (current && !options.some((o) => o.value === current)) {
		options.push({ value: current, version: current, title: null, kind: "current" });
	}
	return options;
}

/** What a new dataspace starts on: the latest release that can run, else `wip`. */
export function defaultVersion(versions: ProjectVersionResponse[]): string {
	return versions.find((v) => v.kind === "release")?.version ?? WIP;
}

/** Whether `e` is the server refusing a version with no build on it. */
export function isVersionNotBuilt(e: unknown): boolean {
	return e instanceof ApiCallError && e.code === API_ERROR_CODES.versionNotBuilt;
}
